import React, { useContext } from 'react';
import { buildClassName } from 'utils/build-class-name';
import { ModalContext, IModalContext, LOGIN_REGISTRATION_MODAL, TERMS_OF_USE_MODAL } from 'contexts/modal';
import { UserContext, IUserContext } from 'contexts/user';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import styles from './style.module.scss';
import { listingPageModalIds } from 'constants/test-constants';
import { TryScrambleText } from 'utils/text-scrambler';
import InfoIcon from 'components/icon/info-icon';
import { ProvinceOrStateCode, isUsStateCode, isCanadianProvinceCode } from 'utils/province_or_state';
import { useTranslation } from 'next-i18next';
import { LOCALE_MODAL_PATHS, LOCALE_LISTING_PATHS, LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useThemeContext } from 'contexts';
import { ThemeNames } from 'types/themes';

export interface ListingPriceData {
  statusLabel: string;
  isTreb: boolean;
  isCrea: boolean;
  isItsoVow: boolean;
  isPillar9Vow: boolean;
  isRebgvVow: boolean;
  providerId: number | null;
  soldPrice: number | null;
  price: number | null;
  isSoldWithoutSoldPrice: boolean;
  isPrivate: boolean;
  agreementType: string | undefined;
  isSignInAndTermsOfUseRequired: boolean;
  province: string | null;
}
interface Props {
  listing: ListingPriceData;
  isVisible: boolean;
  isMinimal?: boolean;
  isSmallCard?: boolean;
  testId?: string;
  shouldScramble: boolean;
  showsInfoIcon?: boolean;
  isExpTheme?: boolean;
}

export default function ListingPrice({ listing, isVisible, isMinimal = false, isSmallCard = false, testId, shouldScramble, showsInfoIcon, isExpTheme }: Props) {
  const { statusLabel, isTreb, soldPrice, isSoldWithoutSoldPrice, price, isPrivate: listingIsPrivate, isCrea, isSignInAndTermsOfUseRequired, providerId } = listing;
  const { isAuthenticated } = useContext(UserContext) as IUserContext;
  const isPrivate = !isAuthenticated && listingIsPrivate;
  const isInactive = statusLabel === 'Inactive';
  const { openModal } = useContext(ModalContext) as IModalContext;
  const { themeName } = useThemeContext();
  const isExpThemeStyle = isExpTheme || (themeName && themeName !== ThemeNames.ZOOCASA);
  const { t } = useTranslation([Namespaces.COMMON, Namespaces.LISTING]);
  const { formatCurrency } = useI18nFormatting();
  const hiddenPriceLabel = isExpThemeStyle ? '$--,---,---' : '$X';

  const openSignInModal = () => {
    if (isPrivate || (isSignInAndTermsOfUseRequired && !isVisible && !isAuthenticated)) {
      openModal(LOGIN_REGISTRATION_MODAL, { warningMessage: t(LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_FORM_SIGNIN_REQUIRED_WARNING) });
    } else if (!isVisible) {
      openModal(TERMS_OF_USE_MODAL, { providerId });
    }
  };

  const renderSignInRequiredLabel = () => {
    if ((isPrivate || (isSignInAndTermsOfUseRequired && !isVisible && !isAuthenticated)) && !isMinimal) {
      return (
        <section className={styles.cta} onClick={openSignInModal}>
          <span className={isExpThemeStyle && styles['sign-in-exp']}>
            {t(LOCALE_COMMON_PATHS.COMPLIANCE_SIGN_IN, { defaultValue: 'Sign in' })}
          </span>{' '}
          {t(LOCALE_COMMON_PATHS.COMPLIANCE_TO_SEE_PRICE, { defaultValue: 'to see price' })}
        </section>
      );
    } else if (((isCrea && !isVisible) || (isSignInAndTermsOfUseRequired && !isVisible)) && !isMinimal) {
      return (
        <section className={styles.cta} onClick={openSignInModal}>
          <span className={isExpThemeStyle && styles['sign-in-exp']}>
            {t(LOCALE_COMMON_PATHS.COMPLIANCE_ACCEPT_TERMS_OF_USE, { defaultValue: 'Accept Terms of Use' })}
          </span>{' '}
          {t(LOCALE_COMMON_PATHS.COMPLIANCE_TO_SEE_PRICE, { defaultValue: 'to see price' })}
        </section>
      );
    }
  };

  const hiddenListingPrice = (price: number | null) => {
    const displayPrice = price === 0 ? '$--' : formatCurrency(price || 0);
    return (
      <div className={buildClassName(styles.price, isSoldWithoutSoldPrice && styles['list-price-only'])} data-testid={testId}>
        {isSoldWithoutSoldPrice && 'List: '}{!isVisible ? hiddenPriceLabel : displayPrice}
      </div>
    );
  };

  const displayCountryCurrency = (stateCode: string) => {
    const lowerCaseCode = stateCode.toLowerCase();
    if (isUsStateCode(lowerCaseCode as ProvinceOrStateCode)) {
      return 'USD';
    } else if (isCanadianProvinceCode(lowerCaseCode as ProvinceOrStateCode)) {
      return 'CAD';
    }
  };

  return isInactive && !isTreb ? null :
    <div className={buildClassName(styles.component, isMinimal && styles.small, isSmallCard && styles['small-card'], isExpThemeStyle && styles.exp)}>
      {listing.province && isVisible &&
        <div className={styles['info-icon-wrapper']}>
          {showsInfoIcon && <InfoIcon className={styles['info-icon']}/>}
          <div className={styles['info-icon-content']}>
            {t(LOCALE_LISTING_PATHS.PRICE_TOOLTIP_LISTED_PRICE_IN_CURRENCY, { 
              currency: displayCountryCurrency(listing.province),
              defaultValue: `Listed price in listed location currency (${displayCountryCurrency(listing.province)}).`,
            })}
            {isSoldWithoutSoldPrice &&
              <>
                <br/><br/>{t(LOCALE_LISTING_PATHS.PRICE_TOOLTIP_ORIGINAL_LISTED_PRICE, {
                  defaultValue: 'You are viewing the original listed price (sold price unavailable).',
                })}
              </>
            }
          </div>
        </div>
      }
      {!!soldPrice && (
        <div className={styles['sold-wrapper']}>
          {hiddenListingPrice(soldPrice)}
          {!(isMinimal && !isVisible) && price &&
            <div className={styles['list-price']}>
              <span data-testid={listingPageModalIds.soldListingListedPrice} className={!isVisible ? 'blur' : ''}>{TryScrambleText(formatCurrency(price), shouldScramble)}</span>
            </div>
          }
        </div>
      )}
      {!soldPrice && price !== null && (
        <div>
          {hiddenListingPrice(price)}
          {renderSignInRequiredLabel()}
        </div>
      )}
    </div>;
}
